/**
 * LegalDocument — renders one of the legal texts in src/legal/*.md.
 *
 * The documents are published EXACTLY as reviewed: the page imports the markdown file's
 * text and this component lays it out. Nothing is retyped into JSX, so a diff of the .md
 * file against the reviewed copy is the whole audit.
 *
 * The renderer covers only what those four documents use, deliberately, so a construct
 * that is not covered shows up in review instead of silently disappearing:
 *   #, ##, ###   headings
 *   ---          rule
 *   * item       bulleted list (consecutive lines)
 *   1. item      numbered list (consecutive lines; the number written in the file is kept)
 *   blank line   paragraph break; consecutive non-blank lines stay in ONE paragraph with
 *                a line break between them (the address blocks)
 *   **bold**, `code`   inline
 *
 * `links` turns a phrase into an in-app link ("Privacy Policy" -> /privacy) without
 * touching the words. The document's own title is never linked.
 */

import React from 'react';
import { Link } from 'react-router-dom';

const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`)/g;

function linkify(text, links, keyBase) {
  if (!links || !links.length) return text;
  const out = [];
  let rest = text;
  let k = 0;
  while (rest.length) {
    let best = null;
    for (const l of links) {
      const i = rest.indexOf(l.phrase);
      if (i !== -1 && (best === null || i < best.i)) best = { i, l };
    }
    if (!best) {
      out.push(rest);
      break;
    }
    if (best.i > 0) out.push(rest.slice(0, best.i));
    out.push(
      <Link key={`${keyBase}-l${k++}`} to={best.l.to} className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2">
        {best.l.phrase}
      </Link>
    );
    rest = rest.slice(best.i + best.l.phrase.length);
  }
  return out;
}

// A run of text that may span lines (a paragraph is one string joined with '\n', so a
// bold that opens on one line and closes on the next — the liability cap — stays one
// bold). Each line break becomes <br/>.
function withBreaks(text, links, keyBase) {
  return text.split('\n').map((ln, j) => (
    <React.Fragment key={`${keyBase}-n${j}`}>
      {j > 0 && <br />}
      {linkify(ln, links, `${keyBase}-n${j}`)}
    </React.Fragment>
  ));
}

function inline(text, links, keyBase) {
  const parts = text.split(INLINE_RE);
  return parts.map((p, i) => {
    if (!p) return null;
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={`${keyBase}-b${i}`} className="font-semibold text-slate-900">{withBreaks(p.slice(2, -2), [], `${keyBase}-b${i}`)}</strong>;
    }
    if (p.startsWith('`') && p.endsWith('`')) {
      return <code key={`${keyBase}-c${i}`} className="px-1 py-0.5 rounded bg-slate-100 text-slate-800 text-[0.9em]">{p.slice(1, -1)}</code>;
    }
    return <React.Fragment key={`${keyBase}-t${i}`}>{withBreaks(p, links, `${keyBase}-t${i}`)}</React.Fragment>;
  });
}

export function parseLegalMarkdown(md) {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i += 1; continue; }
    let m;
    if ((m = /^(#{1,3}) (.+)$/.exec(line))) {
      blocks.push({ type: `h${m[1].length}`, text: m[2] });
      i += 1;
    } else if (/^---$/.test(line.trim())) {
      blocks.push({ type: 'hr' });
      i += 1;
    } else if (/^\* /.test(line)) {
      const items = [];
      while (i < lines.length && /^\* /.test(lines[i])) { items.push(lines[i].slice(2)); i += 1; }
      blocks.push({ type: 'ul', items });
    } else if (/^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && (m = /^(\d+)\. (.*)$/.exec(lines[i]))) { items.push({ n: m[1], text: m[2] }); i += 1; }
      blocks.push({ type: 'ol', items });
    } else {
      const para = [];
      while (i < lines.length && lines[i].trim() && !/^(#{1,3} |\* |\d+\. |---$)/.test(lines[i])) { para.push(lines[i]); i += 1; }
      blocks.push({ type: 'p', lines: para });
    }
  }
  return blocks;
}

const LegalDocument = ({ markdown, links = [], testId = 'legal' }) => {
  const blocks = parseLegalMarkdown(markdown);
  let section = 0;
  return (
    <div className="max-w-none text-slate-700 leading-relaxed" data-testid={`${testId}-document`}>
      {blocks.map((b, idx) => {
        const key = `${testId}-${idx}`;
        switch (b.type) {
          case 'h1':
            return (
              <h1 key={key} className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 tracking-tight" data-testid={`${testId}-title`}>
                {b.text}
              </h1>
            );
          case 'h2':
            section += 1;
            return (
              <h2 key={key} className="text-lg sm:text-xl font-semibold text-slate-900 mt-10 mb-3" data-testid={`${testId}-section-${section}`}>
                {inline(b.text, [], key)}
              </h2>
            );
          case 'h3':
            return <h3 key={key} className="text-base font-semibold text-slate-800 mt-6 mb-2">{inline(b.text, [], key)}</h3>;
          case 'hr':
            return <hr key={key} className="my-8 border-slate-200" />;
          case 'ul':
            return (
              <ul key={key} className="list-disc pl-6 my-3 space-y-1">
                {b.items.map((it, j) => <li key={`${key}-${j}`}>{inline(it, links, `${key}-${j}`)}</li>)}
              </ul>
            );
          case 'ol':
            return (
              <ol key={key} className="list-none pl-0 my-3 space-y-1">
                {b.items.map((it, j) => (
                  <li key={`${key}-${j}`} className="flex gap-2">
                    <span className="shrink-0 tabular-nums">{it.n}.{' '}</span>
                    <span>{inline(it.text, links, `${key}-${j}`)}</span>
                  </li>
                ))}
              </ol>
            );
          default:
            return (
              <p key={key} className="my-3">
                {inline(b.lines.join('\n'), links, key)}
              </p>
            );
        }
      })}
    </div>
  );
};

export default LegalDocument;
